<script setup lang="ts">
import BravoDialog from '@services/ui-component-library/components/BravoDialog.vue';
import BravoButton from '@services/ui-component-library/components/BravoButton.vue';
import BravoInputText from '@services/ui-component-library/components/BravoInputText.vue';
import SafeServerHighlightedText from '@/components/SafeServerHighlightedText.vue';
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useOrganizationsAPI } from '@/composables/services/useOrganizationsAPI';
import type { Organization, PartnerUser } from '@/composables/services/types/organizations';

const props = defineProps<{
    visible: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
    (e: 'confirm', payload: { userId: string; userName: string; orgId: string; orgName: string }): void;
}>();

const api = useOrganizationsAPI();

// ── Step ─────────────────────────────────────────────────────────────────────
type Step = 'org' | 'user';
const step = ref<Step>('org');

// ── Org picker ────────────────────────────────────────────────────────────────
const orgSearch = ref('');
const orgResults = ref<Organization[]>([]);
const orgLoading = ref(false);
const orgTotal = ref(0);
const orgPage = ref(1);
const ORG_PAGE_SIZE = 25;
const selectedOrg = ref<Organization | null>(null);
const orgListRef = ref<HTMLElement | null>(null);

let orgSearchTimer: ReturnType<typeof setTimeout> | null = null;

const loadOrgs = async (reset = false) => {
    if (orgLoading.value && !reset) return;
    if (reset) orgPage.value = 1;
    orgLoading.value = true;
    try {
        const { results, totalCount } = await api.fetchOrganizations({
            page: orgPage.value,
            start: (orgPage.value - 1) * ORG_PAGE_SIZE,
            limit: ORG_PAGE_SIZE,
            query: orgSearch.value.trim() || undefined,
        });
        orgResults.value = reset ? results : [...orgResults.value, ...results];
        orgTotal.value = totalCount;
    } catch {
        // errors are logged in the composable
    } finally {
        orgLoading.value = false;
    }
};

watch(orgSearch, () => {
    if (orgSearchTimer) clearTimeout(orgSearchTimer);
    orgSearchTimer = setTimeout(() => loadOrgs(true), 300);
});

const onOrgScroll = (e: Event) => {
    const el = e.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
    if (nearBottom && !orgLoading.value && orgResults.value.length < orgTotal.value) {
        orgPage.value++;
        loadOrgs();
    }
};

const selectOrg = (org: Organization) => {
    selectedOrg.value = org;
};

const nextStep = () => {
    if (!selectedOrg.value) return;
    step.value = 'user';
    loadUsers(true);
};

// ── User picker ───────────────────────────────────────────────────────────────
const userSearch = ref('');
const userResults = ref<PartnerUser[]>([]);
const userLoading = ref(false);
const userTotal = ref(0);
const userPage = ref(1);
const USER_PAGE_SIZE = 25;
const selectedUser = ref<PartnerUser | null>(null);
const userListRef = ref<HTMLElement | null>(null);

let userSearchTimer: ReturnType<typeof setTimeout> | null = null;

const loadUsers = async (reset = false) => {
    if (!selectedOrg.value) return;
    if (userLoading.value && !reset) return;
    if (reset) userPage.value = 1;
    userLoading.value = true;
    try {
        const { results, totalCount } = await api.fetchUsers({
            orgId: selectedOrg.value.id,
            page: userPage.value,
            start: (userPage.value - 1) * USER_PAGE_SIZE,
            limit: USER_PAGE_SIZE,
            query: userSearch.value.trim() || undefined,
        });
        userResults.value = reset ? results : [...userResults.value, ...results];
        userTotal.value = totalCount;
    } catch {
        // errors are logged in the composable
    } finally {
        userLoading.value = false;
    }
};

watch(userSearch, () => {
    if (userSearchTimer) clearTimeout(userSearchTimer);
    userSearchTimer = setTimeout(() => loadUsers(true), 300);
});

const onUserScroll = (e: Event) => {
    const el = e.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
    if (nearBottom && !userLoading.value && userResults.value.length < userTotal.value) {
        userPage.value++;
        loadUsers();
    }
};

const selectUser = (user: PartnerUser) => {
    selectedUser.value = user;
};

// ── Actions ───────────────────────────────────────────────────────────────────
const back = () => {
    step.value = 'org';
    selectedUser.value = null;
    userSearch.value = '';
    userResults.value = [];
};

const confirm = () => {
    if (!selectedOrg.value || !selectedUser.value) return;
    const userName = [selectedUser.value.first_name, selectedUser.value.last_name]
        .filter(Boolean)
        .join(' ') || selectedUser.value.email;
    emit('confirm', {
        userId: selectedUser.value.id,
        userName,
        orgId: selectedOrg.value.id,
        orgName: selectedOrg.value.name,
    });
    closeDialog();
};

const closeDialog = () => {
    emit('update:visible', false);
};

// ── Reset state helper ────────────────────────────────────────────────────────
const resetState = () => {
    step.value = 'org';
    orgSearch.value = '';
    orgResults.value = [];
    orgTotal.value = 0;
    orgPage.value = 1;
    selectedOrg.value = null;
    userSearch.value = '';
    userResults.value = [];
    userTotal.value = 0;
    userPage.value = 1;
    selectedUser.value = null;
};

// Load initial org list on mount (component is always mounted with visible=true via v-if)
onMounted(() => {
    resetState();
    loadOrgs(true);
});

// Re-populate if the parent re-opens the modal (v-show scenario, future-proofing)
watch(
    () => props.visible,
    (val, prev) => {
        if (val && !prev) {
            resetState();
            loadOrgs(true);
        }
    },
);

onBeforeUnmount(() => {
    if (orgSearchTimer) clearTimeout(orgSearchTimer);
    if (userSearchTimer) clearTimeout(userSearchTimer);
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const initials = (name: string) =>
    name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('');

const userDisplayName = (user: PartnerUser) =>
    [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
</script>

<template>
    <BravoDialog
        :visible="visible"
        modal
        :header="step === 'org' ? 'Select an Organization' : `Select a User from ${selectedOrg?.name}`"
        :style="{ width: '520px' }"
        :closable="true"
        @update:visible="closeDialog"
    >
        <!-- ── Step 1: Org picker ─────────────────────────────────────────── -->
        <div v-if="step === 'org'" class="picker-panel">
            <BravoInputText
                v-model="orgSearch"
                placeholder="Search organizations..."
                class="picker-search"
                :pt="{ root: { class: 'picker-search-input' } }"
            />

            <div class="picker-list-container">
                <div v-if="orgLoading && orgResults.length === 0" class="picker-loading">
                    <i class="pi pi-spinner pi-spin" />
                    <span>Loading organizations...</span>
                </div>

                <div v-else-if="!orgLoading && orgResults.length === 0" class="picker-empty">
                    No organizations found
                </div>

                <ul
                    v-else
                    ref="orgListRef"
                    class="picker-list"
                    role="listbox"
                    aria-label="Organizations"
                    @scroll="onOrgScroll"
                >
                    <li
                        v-for="org in orgResults"
                        :key="org.id"
                        class="picker-item"
                        :class="{ 'is-selected': selectedOrg?.id === org.id }"
                        role="option"
                        :aria-selected="selectedOrg?.id === org.id"
                        @click="selectOrg(org)"
                    >
                        <div class="item-avatar">
                            <img v-if="org.avatar?.trim() && org.url_avatar" :src="org.url_avatar" :alt="org.name" />
                            <span v-else>{{ initials(org.name) }}</span>
                        </div>
                        <div class="item-content">
                            <span class="item-name">
                                <SafeServerHighlightedText :text="org.name" />
                            </span>
                            <span v-if="org.c__user_count != null" class="item-meta">
                                {{ org.c__user_count }} {{ org.c__user_count === 1 ? 'user' : 'users' }}
                            </span>
                        </div>
                        <i v-if="selectedOrg?.id === org.id" class="pi pi-check item-check" />
                    </li>

                    <li v-if="orgLoading && orgResults.length > 0" class="picker-loading-more-row">
                        <i class="pi pi-spinner pi-spin" />
                    </li>
                    <li v-else-if="orgResults.length >= orgTotal && orgResults.length > 0" class="picker-end-row">
                        All {{ orgTotal }} organizations loaded
                    </li>
                </ul>
            </div>
        </div>

        <!-- ── Step 2: User picker ───────────────────────────────────────── -->
        <div v-else class="picker-panel">
            <BravoInputText
                v-model="userSearch"
                placeholder="Search users..."
                class="picker-search"
                :pt="{ root: { class: 'picker-search-input' } }"
            />

            <div class="picker-list-container">
                <div v-if="userLoading && userResults.length === 0" class="picker-loading">
                    <i class="pi pi-spinner pi-spin" />
                    <span>Loading users...</span>
                </div>

                <div v-else-if="!userLoading && userResults.length === 0" class="picker-empty">
                    No users found in this organization
                </div>

                <ul
                    v-else
                    ref="userListRef"
                    class="picker-list"
                    role="listbox"
                    aria-label="Users"
                    @scroll="onUserScroll"
                >
                    <li
                        v-for="user in userResults"
                        :key="user.id"
                        class="picker-item"
                        :class="{ 'is-selected': selectedUser?.id === user.id }"
                        role="option"
                        :aria-selected="selectedUser?.id === user.id"
                        @click="selectUser(user)"
                    >
                        <div class="item-avatar">
                            <img v-if="user.avatar?.trim() && user.url_avatar" :src="user.url_avatar" :alt="userDisplayName(user)" />
                            <span v-else>{{ initials(userDisplayName(user)) }}</span>
                        </div>
                        <div class="item-content">
                            <span class="item-name">
                                <SafeServerHighlightedText :text="userDisplayName(user)" />
                            </span>
                            <span class="item-meta">
                                <SafeServerHighlightedText :text="user.email" />
                            </span>
                        </div>
                        <i v-if="selectedUser?.id === user.id" class="pi pi-check item-check" />
                    </li>

                    <li v-if="userLoading && userResults.length > 0" class="picker-loading-more-row">
                        <i class="pi pi-spinner pi-spin" />
                    </li>
                    <li v-else-if="userResults.length >= userTotal && userResults.length > 0" class="picker-end-row">
                        All {{ userTotal }} users loaded
                    </li>
                </ul>
            </div>
        </div>

        <template #footer>
            <div class="dialog-footer">
                <BravoButton
                    v-if="step === 'org'"
                    label="Cancel"
                    severity="secondary"
                    @click="closeDialog"
                />
                <BravoButton
                    v-if="step === 'user'"
                    label="Back"
                    severity="secondary"
                    icon="pi pi-arrow-left"
                    @click="back"
                />
                <BravoButton
                    v-if="step === 'org'"
                    label="Next"
                    icon="pi pi-arrow-right"
                    icon-pos="right"
                    :disabled="!selectedOrg"
                    @click="nextStep"
                />
                <BravoButton
                    v-if="step === 'user'"
                    label="Launch as User"
                    icon="pi pi-external-link"
                    icon-pos="right"
                    :disabled="!selectedUser"
                    @click="confirm"
                />
            </div>
        </template>
    </BravoDialog>
</template>

<style scoped>
.picker-panel {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.25rem 0.125rem;
}

.picker-search {
    width: 100%;
}

:deep(.picker-search-input) {
    width: 100%;
}

.picker-list-container {
    border: 1px solid var(--surface-border, #dee2e6);
    border-radius: 8px;
    overflow: hidden;
    min-height: 200px;
    max-height: 340px;
    display: flex;
    flex-direction: column;
}

.picker-list {
    list-style: none;
    margin: 0;
    padding: 0.25rem;
    flex: 1;
    overflow-y: auto;
    max-height: 340px;
}

.picker-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.12s ease;
    user-select: none;
}

.picker-item:hover {
    background: var(--surface-hover, #f4f4f5);
}

.picker-item.is-selected {
    background: color-mix(in srgb, var(--primary-color, #4f46e5) 10%, transparent);
}

.item-avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: color-mix(in srgb, var(--primary-color, #4f46e5) 18%, transparent);
    color: var(--primary-color, #4f46e5);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8125rem;
    font-weight: 600;
    overflow: hidden;
}

.item-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.item-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
}

.item-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-color, #1a1a1a);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.item-meta {
    font-size: 0.8rem;
    color: var(--text-color-secondary, #6b7280);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.item-check {
    flex-shrink: 0;
    color: var(--primary-color, #4f46e5);
    font-size: 0.875rem;
}

.picker-loading,
.picker-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 2rem 1rem;
    color: var(--text-color-secondary, #6b7280);
    font-size: 0.9rem;
    flex: 1;
}

.picker-loading-more-row,
.picker-end-row {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    font-size: 0.8rem;
    color: var(--text-color-secondary, #9ca3af);
    list-style: none;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}
</style>
